import { env } from '@mc/runtime-env'

const APPLE_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string
          redirectURI: string
          scope: string
          state: string
          usePopup: boolean
        }) => void
        signIn: () => Promise<AppleSignInResponse>
      }
    }
  }
}

export interface AppleSignInResponse {
  authorization: {
    id_token: string
    code: string
    state: string
  }
  user?: {
    name?: { firstName?: string; lastName?: string }
    email?: string
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load Apple SDK`))
    document.head.appendChild(script)
  })
}

export async function initAndSignInWithApple(): Promise<AppleSignInResponse> {
  const clientId = env('NEXT_PUBLIC_APPLE_SIGN_IN_CLIENT_ID_WEB')
  const redirectURI = env('NEXT_PUBLIC_APPLE_SIGN_IN_WEB_REDIRECT_URI')

  if (!clientId) {
    throw new Error('Apple Sign In is not configured')
  }

  await loadScript(APPLE_SDK_URL)

  if (!window.AppleID) {
    throw new Error('Apple JS SDK failed to load')
  }

  const state = crypto.randomUUID()

  window.AppleID.auth.init({
    clientId,
    redirectURI: redirectURI || '',
    scope: 'name email',
    state,
    usePopup: true,
  })

  return window.AppleID.auth.signIn()
}
